import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authedFetch, hasStoredSession, useStoredAuth, type StoredAuth } from "nazare-ui/utils";
import { resolveBffDomain } from "./bff-domain";

/**
 * Real BFF client for the self-scoped "edit your own profile" surface —
 * `/user/me` in the `notifications-admin` BFF worktree
 * (services/user/index.ts, part of the `nazare-user` service — renamed
 * from the old self-only `nazare-profile` package so its read/update
 * logic is shared with that service's admin-scoped `/user/:id` routes),
 * following the same authed-fetch pattern as notifications-admin-api.ts.
 * Unlike the admin routes, every call here is self-scoped (no `userId`
 * param) — the BFF derives the caller's own row from the JWT, never
 * another user's.
 */

export type Profile = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar: string | null;
};

export type ProfileEdit = {
  firstName: string;
  lastName: string;
  /** `null` clears back to no custom avatar; omitted leaves it unchanged —
   * mirrors the BFF's `UpdateUserSchema` (services/user/user-service.ts).
   * A cropped avatar is sent as a base64 `data:image/jpeg` URL (see
   * ./avatar-upload-modal.tsx) — the column also still accepts a plain
   * https URL for any avatar set before this app had its own upload flow. */
  avatar?: string | null;
};

const PROFILE_BASE = `${resolveBffDomain()}/user`;

export { SessionExpiredError, hasStoredSession } from "nazare-ui/utils";

const profileKey = ["profile", "me"];

async function fetchProfile(auth: StoredAuth | null): Promise<{ profile: Profile }> {
  const res = await authedFetch(PROFILE_BASE, auth, "/me");
  if (!res.ok) throw new Error(`Failed to fetch profile: ${res.status}`);
  return res.json();
}

export function useProfile() {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: profileKey,
    queryFn: () => fetchProfile(auth),
    enabled: hasStoredSession(auth),
  });
}

export function useUpdateProfile() {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (edit: ProfileEdit) => {
      const res = await authedFetch(PROFILE_BASE, auth, "/me", {
        method: "PUT",
        body: JSON.stringify(edit),
      });
      if (!res.ok) throw new Error(`Failed to update profile: ${res.status}`);
      return res.json() as Promise<{ profile: Profile }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKey });
    },
  });
}
